import type { Schema } from '@/shared/api/contract';

/**
 * Straight from the contract: cubas.domain.schemas.quote.
 *
 * **Decimals travel as strings**, and stay strings all the way to the screen. `0.1 + 0.2`
 * is not `0.3` in binary floating point, and a quote a cent out is a quote somebody has to
 * explain to a customer. The front formats them and never does arithmetic on them: the
 * totals come computed from the API, which uses `Decimal`.
 */
export type QuoteStatus = Schema<'QuoteStatus'>;
export type QuoteLine = Schema<'QuoteLineRead'>;
export type QuoteLineWrite = Schema<'QuoteLineWrite'>;
export type QuoteLinesWrite = Schema<'QuoteLinesWrite'>;
export type Quote = Schema<'QuoteRead'>;
export type QuoteCreate = Schema<'QuoteCreate'>;
export type QuoteUpdate = Schema<'QuoteUpdate'>;
export type QuotePdfLink = Schema<'QuotePdfLink'>;
export type QuoteCopies = Schema<'QuoteCopies'>;
export type QuoteWarningsWrite = Schema<'QuoteWarningsWrite'>;
export type QuoteWarningPatch = Schema<'QuoteWarningPatch'>;
export type WarningType = Schema<'WarningType'>;
export type QuoteSendWrite = Schema<'QuoteSendWrite'>;
export type QuoteSending = Schema<'QuoteSendingRead'>;
export type QuoteSendDefaults = Schema<'QuoteSendDefaults'>;

export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  draft: 'Borrador',
  reviewed: 'Revisado',
  sent: 'Enviado',
  cancelled: 'Anulado',
};

/**
 * The supplier's notes, by type. The keys are `WarningType` **from the contract** (v10.6):
 * a type the back adds and this map does not name fails `tsc`, instead of printing a raw
 * `cambio_tarifa` on a badge until somebody notices.
 *
 * Every one of them is shown, and none is ever hidden: if the supplier wrote it, it is
 * because it matters (contexto §4.4). What Víctor decides is whether each goes into the
 * document, and that is the checkbox, not whether it is on screen.
 */
export const WARNING_LABELS: Record<WarningType, string> = {
  sustitucion: 'Sustitución de referencia',
  precio_especial: 'Precio especial',
  fecha_servicio: 'Fecha de servicio',
  validez: 'Validez',
  stock_limitado: 'Stock limitado',
  plazo_entrega: 'Plazo de entrega',
  cambio_tarifa: 'Cambio de tarifa',
  pedido_minimo: 'Pedido mínimo',
  portes: 'Portes',
  descatalogado: 'Descatalogado',
  otro: 'Nota',
};

/**
 * One entry of `quotes.warnings`, **from the contract** since v6.
 *
 * It used to be transcribed by hand here, with the labels copied from the back. A label
 * copied is a label that drifts the first time the back adds a `type`, and it drifts
 * silently, which is the worst way for a note on a customer's quote to go wrong.
 */
export type QuoteWarning = NonNullable<Quote['warnings']>[number];

export function warningsOf(quote: Quote): QuoteWarning[] {
  return quote.warnings ?? [];
}

/** The types Víctor can pick when he rewrites a note, in the contract's order. */
export const WARNING_TYPES = Object.keys(WARNING_LABELS) as WarningType[];

/**
 * The label for a note's type. Stored notes are JSONB and older rows could carry a type
 * this version does not know; those read as a plain note rather than crashing the card.
 */
export function warningLabel(type: string | null | undefined): string {
  return (WARNING_LABELS as Record<string, string | undefined>)[type ?? 'otro'] ?? 'Nota';
}

/** Only a draft can be edited. The API answers 409 to anything else. */
export function isEditable(quote: Quote): boolean {
  return quote.status === 'draft';
}
